import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type StaffPhotoPose, staffPhotoClient } from "@users/staffPhotoClient";

const staffPhotoKeys = {
  all: ["staff-photo"] as const,
  mine: (sessionKey: string) => ["staff-photo", sessionKey] as const,
  coverage: (sessionKey: string) =>
    ["staff-photo", "coverage", sessionKey] as const,
};

export const useMyStaffPhotos = (sessionKey: string) =>
  useQuery({
    queryKey: staffPhotoKeys.mine(sessionKey),
    queryFn: staffPhotoClient.getMine,
  });

/** Leadership only. */
export const useStaffPhotoCoverage = (sessionKey: string, enabled: boolean) =>
  useQuery({
    queryKey: staffPhotoKeys.coverage(sessionKey),
    queryFn: staffPhotoClient.coverage,
    enabled,
  });

const useInvalidating = <T>(
  mutationFn: (input: T) => Promise<unknown>,
  action: string,
  handled = false,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    meta: { action, handled },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: staffPhotoKeys.all }),
  });
};

/** `handled`: the caller shows the failure inline instead of the global notice. */
export const useUploadStaffPhoto = ({ handled = false } = {}) =>
  useInvalidating(
    ({
      file,
      consentVersion,
      pose,
      guided,
    }: {
      file: Blob;
      consentVersion: string;
      pose: StaffPhotoPose;
      guided: boolean;
    }) => staffPhotoClient.upload(file, consentVersion, pose, guided),
    "save your photo",
    handled,
  );

export const useConfirmStaffPhoto = () =>
  useInvalidating(
    ({
      photoId,
      consentVersion,
    }: {
      photoId: string;
      consentVersion: string;
    }) => staffPhotoClient.confirm(photoId, consentVersion),
    "use this photo",
  );

export const useDeleteStaffPhoto = () =>
  useInvalidating(
    (photoId: string) => staffPhotoClient.remove(photoId),
    "delete the photo",
  );

export const useConsentToRoomPhotos = () =>
  useInvalidating(
    (consentVersion: string) => staffPhotoClient.consent(consentVersion),
    "save your agreement",
  );

/** `handled`: the capture dialog shows its failure inline. */
export const useCaptureStaffPhotos = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: staffPhotoClient.capture,
    meta: { handled: true },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: staffPhotoKeys.all }),
  });
};
